/**
 * Live money tokens (v9-interactive). When a new money-mover event arrives in the feed,
 * spawns a labelled € token that flies along the SVG topology edge from the engine to the
 * destination bank, then pops (scales up + fades) on arrival. Cross-bank SCT tokens use
 * a direct bank-to-bank arc. Corridor flags (🇺🇸/🇬🇧/🇨🇭/🇯🇵) appear for cross-border events.
 *
 * Each token also carries a small message-type tag (e.g. `pacs.008.001.10`, `pain.008`,
 * `REST`) so a viewer can tell what actually happened, not just how much moved — different
 * scenarios are genuinely different messages, not the same call with a different label.
 * internal_transfer never leaves the bank it happened at, so it travels a small self-loop
 * instead of the engine→bank edge (see loop-evaris_es / loop-evairi_com in initTokenLayer).
 */

import type { RequestEvent } from './feed.js';

const MONEY_SCENARIOS = new Set(['sct', 'sct_inst', 'internal_transfer', 'fx_convert', 'p2p_redeem', 'sdd_mandate']);
const EUR_RE = /€([\d,]+(?:\.\d{1,2})?)/;
const CORRIDOR_FLAG: Record<string, string> = { US: '🇺🇸', GB: '🇬🇧', CH: '🇨🇭', JP: '🇯🇵' };
const XBANK_SCENARIOS = new Set(['sct', 'sct_inst']);

/** Sequences we've already animated — prevents replaying on re-polls. */
const seen = new Set<number>();

function parseCents(desc: string): number {
  const m = EUR_RE.exec(desc);
  if (!m) return 0;
  return Math.round(parseFloat(m[1]!.replace(/,/g, '')) * 100);
}

/** Radius 5–12 px, logarithmically scaled by amount. */
function tokenRadius(cents: number): number {
  if (cents <= 0) return 5;
  return Math.min(12, 5 + Math.log2(cents / 100) * 1.5);
}

function extractFlag(desc: string): string {
  for (const [code, flag] of Object.entries(CORRIDOR_FLAG)) {
    if (desc.includes(code) || desc.includes(flag)) return flag;
  }
  return '';
}

/** Add the hidden cross-bank arc + per-bank self-loop paths to the SVG (called once on init). */
export function initTokenLayer(doc: Document): void {
  const svg = doc.querySelector('svg.topology');
  if (!svg || doc.getElementById('mp-xbank')) return;
  const ns = 'http://www.w3.org/2000/svg';

  const path = doc.createElementNS(ns, 'path');
  path.id = 'mp-xbank';
  path.setAttribute('d', 'M715,210 C775,260 775,280 715,300');
  path.setAttribute('fill', 'none');
  path.setAttribute('stroke', 'none');
  svg.prepend(path);

  // Self-loops: an internal transfer never reaches the other bank, so it gets its own
  // small loop next to each bank node instead of travelling the engine→bank edge.
  const loopEvaris = doc.createElementNS(ns, 'path');
  loopEvaris.id = 'loop-evaris_es';
  loopEvaris.setAttribute('d', 'M675,60 C665,20 755,20 745,60');
  loopEvaris.setAttribute('fill', 'none');
  loopEvaris.setAttribute('stroke', 'none');
  svg.prepend(loopEvaris);

  const loopEvairi = doc.createElementNS(ns, 'path');
  loopEvairi.id = 'loop-evairi_com';
  loopEvairi.setAttribute('d', 'M675,300 C665,265 755,265 745,300');
  loopEvairi.setAttribute('fill', 'none');
  loopEvairi.setAttribute('stroke', 'none');
  svg.prepend(loopEvairi);
}

function spawnToken(svg: SVGSVGElement, pathId: string, label: string, radius: number, color: string, tag: string): void {
  const ns = 'http://www.w3.org/2000/svg';
  const dur = pathId === 'mp-xbank' ? '1.2s' : pathId.startsWith('loop-') ? '1.4s' : '1.8s';

  const g = doc(svg).createElementNS(ns, 'g');
  g.setAttribute('class', 'money-token');
  g.setAttribute('pointer-events', 'none');

  const circle = doc(svg).createElementNS(ns, 'circle');
  circle.setAttribute('r', String(radius));
  circle.setAttribute('fill', color);
  circle.setAttribute('opacity', '0.92');

  const text = doc(svg).createElementNS(ns, 'text');
  text.setAttribute('text-anchor', 'middle');
  text.setAttribute('dominant-baseline', 'middle');
  text.setAttribute('font-size', `${Math.max(7, radius - 1)}px`);
  text.setAttribute('fill', '#fff');
  text.setAttribute('font-weight', '700');
  text.textContent = label;

  const anim = doc(svg).createElementNS(ns, 'animateMotion');
  anim.setAttribute('dur', dur);
  anim.setAttribute('fill', 'freeze');
  anim.setAttribute('calcMode', 'spline');
  anim.setAttribute('keyTimes', '0;1');
  anim.setAttribute('keySplines', '0.4 0 0.6 1');
  // mp-evaris_es / mp-evairi_com terminate right at the bank box's own text column —
  // stop just short so the token (and its tag, floating above it) lands in the open
  // gap before the box instead of on top of the bank's health/concurrency readout.
  if (pathId === 'mp-evaris_es' || pathId === 'mp-evairi_com') {
    anim.setAttribute('keyPoints', '0;0.85');
  }
  const mpath = doc(svg).createElementNS(ns, 'mpath');
  mpath.setAttribute('href', `#${pathId}`);
  anim.append(mpath);

  g.append(circle, text, anim);
  if (tag) {
    const tagText = doc(svg).createElementNS(ns, 'text');
    tagText.setAttribute('text-anchor', 'middle');
    tagText.setAttribute('y', String(-(radius + 5)));
    tagText.setAttribute('class', 'token-tag');
    tagText.textContent = tag;
    g.append(tagText);
  }
  svg.append(g);

  const totalMs = parseFloat(dur) * 1000;
  window.setTimeout(() => {
    g.setAttribute('class', 'money-token popping');
    window.setTimeout(() => g.remove(), 500);
  }, totalMs - 100);
}

function doc(el: Element): Document {
  return el.ownerDocument!;
}

/** Colour + message-type tag per scenario — different operations are genuinely different
 * messages, not the same rail with a different label. ISO 20022 codes for the scenarios
 * that actually cross banks; everything else is a plain REST call. */
function messageMeta(scenario: string): { color: string; tag: string } {
  switch (scenario) {
    case 'sct':
      return { color: '#4c8dff', tag: 'pacs.008.001.10' };
    case 'sct_inst':
      return { color: '#a855f7', tag: 'pacs.008 inst → ACCP' };
    case 'sdd_mandate':
      return { color: '#ec4899', tag: 'pain.008' };
    case 'p2p_redeem':
      return { color: '#06b6d4', tag: 'pacs.010' };
    case 'fx_convert':
      return { color: '#d68a1a', tag: 'REST' };
    case 'internal_transfer':
      return { color: '#93a0b8', tag: 'REST · same-bank' };
    default:
      return { color: '#4c8dff', tag: '' };
  }
}

export function updateTokens(svgEl: SVGSVGElement | null, events: RequestEvent[]): void {
  if (!svgEl) return;
  for (const e of events) {
    if (seen.has(e.seq)) continue;
    if (!MONEY_SCENARIOS.has(e.scenario)) continue;
    seen.add(e.seq);
    if (seen.size > 300) {
      const first = seen.values().next().value;
      if (first !== undefined) seen.delete(first);
    }

    const cents = parseCents(e.description);
    const radius = tokenRadius(cents);
    const eur = cents > 0 ? `€${Math.round(cents / 100)}` : '€';
    const flag = extractFlag(e.description);
    const label = flag ? flag : eur;
    const pathId =
      e.scenario === 'internal_transfer'
        ? e.bank === 'evaris_es' ? 'loop-evaris_es' : 'loop-evairi_com'
        : XBANK_SCENARIOS.has(e.scenario) && e.description.includes('→')
          ? 'mp-xbank'
          : e.bank === 'evaris_es' ? 'mp-evaris_es' : 'mp-evairi_com';
    const { color, tag } = messageMeta(e.scenario);

    spawnToken(svgEl, pathId, label, radius, color, tag);
  }
}
